<template>
    <header class="sticky top-0 z-40 bg-white shadow-md dark:bg-gray-800">
        <nav class="w-full bg-blue-600">
            <div class="mx-auto flex max-w-screen-xl flex-wrap items-center justify-between p-4">
                <p class="m-0 text-sm font-semibold italic text-white dark:text-white">Agarwood Plantations - We Plant, Grow and Harvest</p>

                <div class="invisible h-0 lg:visible lg:h-auto">
                    <div class="flex gap-x-3">
                        <!--<UHeaderLinks
                            :links="topNavBarLinks"
                            :ui="{
                                wrapper: 'flex items-center gap-x-2',
                                base: 'text-xs text-white font-semibold flex items-center gap-1 hover:text-white/80',
                                active: 'text-white',
                                inactive: 'hover:text-white/80'
                            }" />-->

                        <PagesSocialMedia size="xs" color="white" class="text-white" hover="text-white/80" />
                    </div>
                </div>
            </div>
        </nav>

        <UContainer class="flex items-center justify-between py-4">
            <div class="flex items-center">
                <NuxtLink to="/" class="flex items-center lg:mr-6">
                    <NuxtImg width="220px" src="/assets/images/logo.webp" :alt="config.public.company.name" />
                </NuxtLink>
            </div>

            <nav class="hidden items-center gap-6 lg:flex lg:px-4">
                <UHeaderLinks :links="navBarLinks" />
            </nav>

            <div class="flex items-center justify-end gap-4 md:w-[160px] md:gap-6">
                <SlideoverNavigation />
                <SlideoverLogin />

                <UButton class="hidden font-semibold lg:block" label="Client Portal" size="lg" color="green" variant="solid" @click="toggleLogin" />
            </div>
        </UContainer>
    </header>
</template>

<script setup>
    const config = useRuntimeConfig()

    const isShowingLogin = ref(false)
    const loading = ref(false)
    const disabled = ref(false)

    const toggleLogin = () => {
        isShowingLogin.value = !isShowingLogin.value
    }

    provide('login', {
        isShowingLogin,
        toggleLogin,
        loading,
        disabled
    })

    const topNavBarLinks = [
        {
            label: 'Ethics',
            to: '/legal'
        },
        {
            label: '|',
            class: 'cursor-none'
        },
        {
            label: 'Client Portal',
            click: () => toggleLogin()
        }
    ]

    const navBarLinks = [
        {
            label: 'Home',
            to: '/'
        },
        {
            label: 'Plantations',
            to: '/plantations',
            children: [
                {
                    label: 'Agarwood Plantations',
                    to: '/plantations/agarwood-plantations',
                    icon: 'i-ph-plant',
                    description: 'Organic agriculture is a production system that sustains the health of soils.'
                },
                {
                    label: 'Plantation Challenges',
                    to: '/plantations/plantation-challenges',
                    icon: 'i-ph-potted-plant',
                    description: 'Embarking on the journey of agarwood cultivation requires an in-depth understanding.'
                },
                {
                    label: 'Professional Management',
                    to: '/plantations/professional-management',
                    icon: 'i-bi-people',
                    description: 'Professional management companies bring a wealth of experience and specialized knowledge.'
                },
                {
                    label: 'Carbon Neutral Agriculture',
                    to: '/plantations/carbon-neutral-agriculture',
                    icon: 'i-mdi-periodic-table-carbon-dioxide',
                    description: 'We outline our roadmap in achieving Carbon Neutral Agriculture (CNA) on our plantations across Thailand'
                }
            ]
        },
        {
            label: 'Sustainability',
            to: '/sustainability',
            children: [
                {
                    label: 'Agriculture Asset Class',
                    to: '/sustainability/agriculture-asset-class',
                    icon: 'i-bi-graph-up-arrow',
                    description: 'Agriculture as an asset class falls under the broad category of real assets.'
                },
                {
                    label: 'Regenerative Practices',
                    to: '/sustainability/regenerative-practices',
                    icon: 'i-mdi-regenerative-agriculture',
                    description: 'Organic agriculture is a production system that sustains the health of soils.'
                },
                {
                    label: 'Sustainable Future',
                    to: '/sustainability/sustainable-future',
                    icon: 'i-game-icons-stump-regrowth',
                    description: 'Sustainable agricultural or forestry production involves the ongoing ability to produce crops.'
                },
                {
                    label: 'Global Warming Initiative',
                    to: '/sustainability/global-warming-initiative',
                    icon: 'i-bi-sun',
                    description: 'Scientists have spent decades figuring out what is causing global warming.'
                }
            ]
        },
        {
            label: 'Opportunities',
            to: '/opportunities',
            children: [
                {
                    label: 'Plantation Ownership',
                    to: '/opportunities/plantation-ownership',
                    icon: 'i-lucide-trees',
                    description: 'Aquilaria can grow on a wide range of soils, including poor sandy soil.'
                },
                {
                    label: 'Agarwood Oil & Chips',
                    to: '/opportunities/agarwood-oil-and-chips',
                    icon: 'i-carbon-soil-moisture-field',
                    description: 'Its rich scent and exotic associations have made it synonymous with oriental fragrances for centuries.'
                }
            ]
        },
        {
            label: 'Associates',
            to: '/associates',
            children: [
                {
                    label: 'Al Hashimi',
                    to: '/associates/al-hashimi',
                    icon: 'i-hugeicons-perfume',
                    description: 'Connoisseurs of the finest Artisanal Oud to push the boundaries of what is possible with this natural aromatic.'
                },
                {
                    label: 'Ghallia',
                    to: '/associates/ghallia',
                    icon: 'i-cbi-essential-oil-diffuser-alt',
                    description: 'Ghallia is the name of the company under which Al Hashimi perfumes will be launched.'
                },
                {
                    label: 'Ouddict Artisanal Oud Community',
                    to: '/associates/ouddict-artisanal-oud-community',
                    icon: 'i-bi-chat-dots',
                    description: 'Ouddict is the world’s largest online forum dedicated to oud and natural perfumery.'
                }
            ]
        },
        {
            label: 'Corporate',
            to: '/corporate',
            children: [
                {
                    label: 'Book a Discovery Call',
                    to: '/corporate/book-a-discovery-call',
                    icon: 'i-bi-telephone',
                    description: 'Book your free, no obligation consultation with our representative.'
                },
                {
                    label: 'Core Values',
                    to: '/corporate/core-values',
                    icon: 'i-bi-award',
                    description: 'Corporate social responsibility serves as the basis for all our decisions.'
                },
                {
                    label: 'Meet the Team',
                    to: '/corporate/meet-the-team',
                    icon: 'i-bi-people',
                    description: 'Dedicated to producing the world’s best quality sustainable agarwood using permaculture principles.'
                },
                {
                    label: 'Careers',
                    to: '/corporate/careers',
                    icon: 'i-bi-person-plus',
                    description: 'We seek a motivated and ambitious people to join our team.'
                },
                {
                    label: 'Investor Relations',
                    to: '/corporate/investor-relations',
                    icon: 'i-carbon-chart-relationship',
                    description: 'A company dedicated to producing the world’s best quality sustainable agarwood using permaculture principles.'
                },
                {
                    label: 'Contact Us',
                    to: '/corporate/contact-us',
                    icon: 'i-bi-pin-map',
                    description:
                        'Feel free to contact us at any time and ask any questions. We are always open for any suggestions and collaborations'
                }
            ]
        }
    ]
</script>
